import {useState} from 'react';
import {useFormik} from 'formik';

import PricingListCommonWrapperModalComponent from '../PricingListCommonWrapperModalComponent';
import AddSingleNewPrice from '../AddNewPrice/AddSingleNewPrice';
import DeleteConfirmDialog from '~/components/DeleteConfirmDialog';

import {useUpdatePricingEntry, useDeletePricingEntry} from '../hooks';

import {PRICE_COMPONENT_MODES} from '../AddNewPrice/constants';
import {ACTION_CONSTANTS} from './constants';
import {addPriceValidationSchema} from '../AddNewPrice/validations';
import {IAddPriceFormValues} from '../AddNewPrice';
import {EyeInvolvement} from '../constants';

import {
  BarChartSquarePurpleIcon,
  DeleteIcon,
  PencilEditIcon,
} from '~/assets/svgs';

interface IPricingTableActionsHandlerContainerProps {
  rowData: TAllRowTypes;
}

const PricingTableActionsHandlerContainer = ({
  rowData,
}: IPricingTableActionsHandlerContainerProps) => {
  const {DELETE, EDIT} = ACTION_CONSTANTS;

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedAction, setSelectedAction] = useState<string | undefined>();

  const {deletePricingEntryRequest, disableSubmit} = useDeletePricingEntry(
    () => {
      handleCloseClick();
    }
  );

  const toggleIsModalOpen = () => {
    setIsModalOpen(!isModalOpen);
  };

  const handleCloseClick = () => {
    toggleIsModalOpen();
    setSelectedAction(undefined);
  };

  const handleActionClick = (action: string) => {
    setSelectedAction(action);
    toggleIsModalOpen();
  };

  const handleOnDelete = () => {
    deletePricingEntryRequest(Number(rowData.id));
  };

  const {sendUpdatePricingEntryRequest} = useUpdatePricingEntry(() => {
    handleCloseClick();
  });

  const handleOnUpdateClick = (values: IAddPriceFormValues) => {
    const categoryName = values.categoryName;

    let payload: {
      id: string;
      category_id: string;
      doctor_id?: string;
      consultation_fee?: string;
      followup_fee?: string;
      ccsd_code?: string;
      description?: string;
      eye_involvement?: EyeInvolvement | '';
      procedure_price?: string;
    } = {
      id: rowData.id!,
      category_id: String(values.categoryId),
    };

    if (categoryName === 'Consultation') {
      payload = {
        ...payload,
        doctor_id: values.doctorId,
        consultation_fee: values.consultationFee,
        followup_fee: values.followupFee,
      };
    } else {
      payload = {
        ...payload,
        ccsd_code: values.ccsdCode,
        description: values.description,
        procedure_price: String(parseFloat(values.price)),
        ...(categoryName !== 'Diagnostic/Other Fees' && {
          eye_involvement:
            values.eyeOption === 'both'
              ? EyeInvolvement.BothEyes
              : EyeInvolvement.OneEye,
        }),
      };
    }

    sendUpdatePricingEntryRequest(payload);
  };

  const getFormFieldValue = (fieldName: string) =>
    fieldName in rowData ? rowData[fieldName as keyof TAllRowTypes] || '' : '';

  const editFormik = useFormik<IAddPriceFormValues>({
    initialValues: {
      categoryId: rowData?.category_id,
      categoryName: rowData?.category_name || '',
      ccsdCode: getFormFieldValue('ccsd_code'),
      description: getFormFieldValue('description'),
      eyeOption:
        Number(getFormFieldValue('eye_involvement')) === 1 ? 'both' : 'one',
      price: getFormFieldValue('procedure_price'),
      doctorId: getFormFieldValue('doctor_id'),
      consultationFee: getFormFieldValue('consultation_fee'),
      followupFee: getFormFieldValue('followup_fee'),
    },
    validationSchema: addPriceValidationSchema(
      PRICE_COMPONENT_MODES.EDIT_PRICE_MODE
    ),
    onSubmit: handleOnUpdateClick,
    enableReinitialize: true,
  });

  return (
    <>
      <DeleteIcon className="mr-4" onClick={() => handleActionClick(DELETE)} />
      <PencilEditIcon className="" onClick={() => handleActionClick(EDIT)} />
      {selectedAction === DELETE ? (
        <DeleteConfirmDialog
          onClose={() => setSelectedAction(undefined)}
          isDisabled={disableSubmit}
          isOpen={isModalOpen}
          onDelete={handleOnDelete}
          title="price"
        />
      ) : null}
      {selectedAction === EDIT && isModalOpen ? (
        <div className="max-h-[70vh] overflow-y-auto pr-2 space-y-4">
          <PricingListCommonWrapperModalComponent
            isOpen={isModalOpen}
            onClose={handleCloseClick}
            title="Edit price"
            headerIcon={<BarChartSquarePurpleIcon />}
            onPrimaryClick={editFormik.handleSubmit}
            primaryButtonTitle="Save changes"
            isPrimaryDisabled={false}
            isSecondaryVisible={true}
            secondaryButtonTitle="Cancel"
            onSecondaryClick={handleCloseClick}
          >
            <div className="space-y-4">
              <AddSingleNewPrice
                formik={editFormik}
                mode={PRICE_COMPONENT_MODES.EDIT_PRICE_MODE}
              />
            </div>
          </PricingListCommonWrapperModalComponent>
        </div>
      ) : null}
    </>
  );
};

export default PricingTableActionsHandlerContainer;
